import { useMemo, type ReactNode } from 'react'
import { CHART_MAX_ROWS, type ChartDefinition, type TimelineGranularity } from '@solum/experience'
import { InsightChart, type InsightDatum } from './InsightChart'
import { formatTimeBucket } from './chartFormat'
import s from './TimelineChart.module.css'

export interface TimelineBucket {
  start: string
  startLocal: string
  end: string
  endLocal: string
}

export interface TimelinePoint {
  value: number | string | null
  samples: number
}

export interface TimelineSeries {
  key: string
  aggregation: string
  field?: string | null
  label: string
  points: TimelinePoint[]
}

export interface TimelineChartData {
  granularity: TimelineGranularity
  timeZone: string
  zoneApplied: boolean
  buckets: TimelineBucket[]
  series: TimelineSeries[]
}

export type TimelineChartState = 'loading' | 'ready' | 'empty' | 'refused' | 'error'

export interface TimelineChartProps {
  data?: TimelineChartData | null
  state?: TimelineChartState
  problem?: string | null
  /** Optional freshness statement derived by the host, never guessed here. */
  freshness?: ReactNode
  /** Three-dot settings action supplied by the host; this component stores nothing. */
  actions?: ReactNode
  testId?: string
}

const number = (value: TimelinePoint['value']): number | null => {
  if (value == null || value === '') return null
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

/**
 * One relationship Timeline, with chart/table parity.
 *
 * Data-free by design: the host supplies the already-authorised aggregate and
 * the settings action. This component neither fetches rows nor decides which
 * recipe a caller may use. Counts are bars, numeric readings are continuous
 * lines, and a null remains a gap all the way into ECharts.
 */
export function TimelineChart({
  data, state = data ? 'ready' : 'loading', problem, freshness, actions,
  testId = 'timeline-chart',
}: TimelineChartProps) {
  const rows = useMemo(() => {
    if (!data) return [] as InsightDatum[]
    return data.series.flatMap((series) => data.buckets.map((bucket, index) => ({
      bucket: bucket.startLocal,
      series: series.label,
      value: number(series.points[index]?.value ?? null),
      samples: series.points[index]?.samples ?? 0,
    })))
  }, [data])

  const kinds = useMemo(() => Object.fromEntries((data?.series ?? []).map((series) => [
    series.label,
    series.aggregation === 'count' ? 'bar' : 'area',
  ])) as Record<string, 'bar' | 'area'>, [data])

  const definition = useMemo<ChartDefinition | null>(() => data ? ({
    definitionVersion: 1,
    key: 'relationship.timeline',
    revision: 1,
    kind: 'line',
    source: 'core.timeline-series',
    encoding: {
      measure: 'value', timeField: 'bucket', timeGrain: data.granularity, series: 'series',
    },
    title: 'Timeline',
    intent: `Related readings grouped by ${data.granularity}`,
    description: `A ${data.granularity} timeline in ${data.timeZone}`,
    emptyState: 'No readings fall inside this time window.',
    sort: { by: 'time', direction: 'asc' },
    // The Timeline contract admits 744 buckets and four series. The generic
    // chart limit must cover that complete, already-aggregated result.
    limit: Math.min(CHART_MAX_ROWS, data.buckets.length * data.series.length),
  }) : null, [data])

  const hasSamples = data?.series.some((series) => series.points.some((point) => point.samples > 0)) === true
  const effectiveState = state === 'ready' && !hasSamples ? 'empty' : state
  const summary = data
    ? `${data.series.length} ${data.series.length === 1 ? 'series' : 'series'} across ${data.buckets.length} ${data.granularity} buckets. Time zone ${data.timeZone}.`
    : 'Timeline'

  return (
    <section className={s.panel} data-testid={testId} data-state={effectiveState} aria-label="Timeline">
      <header className={s.header}>
        <div>
          <h4 className={s.title}>Timeline</h4>
          {data && <p className={s.meta}>{data.buckets.length} {data.granularity} buckets · {data.timeZone}</p>}
        </div>
        <div className={s.actions}>{freshness}{actions}</div>
      </header>

      {effectiveState === 'loading' && <p className={s.state} role="status">Loading timeline...</p>}
      {effectiveState === 'empty' && <p className={s.state}>No readings fall inside this time window.</p>}
      {effectiveState === 'refused' && <p className={s.state}>This timeline is not available for your current access.</p>}
      {effectiveState === 'error' && <p className={s.state} role="alert">{problem || 'The timeline could not be loaded.'}</p>}
      {effectiveState === 'ready' && data && definition && (
        <>
          <InsightChart definition={definition} rows={rows}
            accessibleDescription={summary} seriesKinds={kinds} testId={`${testId}-plot`} />
          <details className={s.tableWrap}>
            <summary>View timeline data</summary>
            <div className={s.tableScroll}>
              <table className={s.table}>
                <caption>{summary}</caption>
                <thead>
                  <tr><th scope="col">Time</th>{data.series.map((series) => <th scope="col" key={series.key}>{series.label}</th>)}</tr>
                </thead>
                <tbody>
                  {data.buckets.map((bucket, index) => (
                    <tr key={bucket.startLocal}>
                      <th scope="row">{formatTimeBucket(bucket.startLocal, data.granularity)}</th>
                      {data.series.map((series) => {
                        const point = series.points[index]
                        return <td key={series.key} data-samples={point?.samples ?? 0}>
                          {point?.value == null ? 'No reading' : String(point.value)}
                        </td>
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </details>
        </>
      )}
    </section>
  )
}
